import { Injectable, Injector } from '@angular/core';
import { API_ENDPOINT, GLOBAL_DATA_KEY, SESSION_STORAGE_KEY, LOCAL_STORAGE_KEY, SAUCER_LOG_ACTION, APP_CONFIG } from '@core/configs';
import { Mode, OfficeType, ROUTE_PATH, TV } from '@core/constants';
import { lastValueFrom } from 'rxjs';
import {
  SessionStorageService,
  ApiService,
  GlobalDataService,
  LocalStorageService,
  SessionContextService,
  TvDefaultSettingConfigService,
  TvLoginSessionService
} from '@core/services/common';
import { FirebaseDatabaseService } from '../common/firebase/firebase-database.service';
import { AuthService } from './auth.service';
import { MultipleSpinnerService } from '../common/multiple-spinner.service';
import { FirebaseAuthService } from '../common/firebase/firebase-auth.service';
import { OfficeService } from './office.service';
import { ScreenTitleConfigService } from './screen-title-config.service';
import { NavService } from '../common/nav.service';
import { CheckSheetService } from './check-sheet.service';
import { TimeService } from '../common/time.service';
import { DeviceLicensesService } from './device-licenses.service';
import { NativeDiagnosticLogService } from '@core/services/diagnostic-log/native-diagnostic-log.service';
// import { DeviceLogsService } from './device-logs.service';
import { SaucerLogService } from '../saucer-logs/saucer-log.service';

@Injectable({
  providedIn: 'root',
})
export class AfterLoginService {
  private firebaseDatabaseService: FirebaseDatabaseService | undefined;
  private officeType: number = OfficeType.newTsusho;

  constructor(
    private ttlConfigService: ScreenTitleConfigService,
    private sessionStorageService : SessionStorageService,
    private localStorageService : LocalStorageService,
    private globalDataService: GlobalDataService,
    private sessionContextService: SessionContextService,
    private tvLoginSessionService: TvLoginSessionService,
    private authService: AuthService,
    private injector: Injector,
    private spinnerService: MultipleSpinnerService,
    private firebaseAuthService: FirebaseAuthService,
    private officeService: OfficeService,
    private navService: NavService,
    private readonly http: ApiService,
    private checkSheetService: CheckSheetService,
    private timeService: TimeService,
    private deviceLicensesService: DeviceLicensesService,
    private nativeDiagnosticLogService: NativeDiagnosticLogService,
    // private deviceLogsService: DeviceLogsService,
    private tvDefaultSettingConfigService: TvDefaultSettingConfigService,
    private saucerLogService: SaucerLogService
  ) {
  }

  async afterSelectedOffice(officeSelected: any, corpprefix: string): Promise<boolean> {
    this.spinnerService.show();
    try {
      this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.lastSyncDate, officeSelected.latestSyncDate);
      this.sessionContextService.setSelectedOfficeIdentity(officeSelected);

      this.authService.setIsLoggedIn(true);
      this.globalDataService.setGlobalData();
      await this.tvDefaultSettingConfigService.initializeForCurrentOffice();
      // cập nhật thông tin customer đăng nhập theo deviceId (chỉ khi có androidDeviceId)
      // The fixed device token is provisioned by the native app. A staff login
      // only updates the customer credentials associated with this device.
      await this.deviceLicensesService.updateLoginCustomerInfoByDeviceId(
        this.sessionStorageService.getAccessToken(),
      );
      await this.updateLogEnabledForSelectedOffice();
      await this.deviceLicensesService.syncDeviceMetadata(true);
      // await this.deviceLogsService.saveDeviceLog(corpprefix, officeSelected.officeCd);
      const firebaseLoggedIn = await this.firebaseAuthService.loginWithFirebaseToken(corpprefix, officeSelected.officeCd);
      if (APP_CONFIG.useFirebase && !firebaseLoggedIn) {
        throw new Error('Firebase authentication failed.');
      }
      this.firebaseDatabaseService = this.injector.get(FirebaseDatabaseService);
      // lấy office type sau khi chọn office
      await this.getOfficeType(officeSelected?.officeCd);
      await this.ttlConfigService.checkMasterExist();
      // lấy latestSyncDate chính xác từ API trước khi khởi tạo Firebase
      const latestSyncDateRes = await lastValueFrom(this.officeService.getLatestSyncDate(officeSelected?.officeCd, corpprefix));
      const latestSyncDate = latestSyncDateRes?.result?.latestSyncDate ?? "";
      this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.lastSyncDate, latestSyncDate);
      // Khởi tạo Firebase sau khi đã có latestSyncDate chính xác trong sessionStorage
      const firebaseInitialized = await this.firebaseDatabaseService.initializeFirebase();
      if (!firebaseInitialized) {
        throw new Error('Firebase Realtime Database initialization failed.');
      }
      await this.checkLatestSyncDateAndSyncData(latestSyncDate, officeSelected.officeCd, corpprefix);
      await this.deviceLicensesService.syncSettingConfig();
      this.saucerLogService.system(
        {
          apiPath: '',
          statusCode: 200,
          content: JSON.stringify({
            officeSelected
          })
        },
        {
          action: SAUCER_LOG_ACTION.LOGIN.SUCCESS
        }
      );
    } catch (error) {
      console.error('After selecting office failed:', error);
        this.saucerLogService.error(
        {
          apiPath: '',
          statusCode: 500,
          errorMessage: error,
          content: JSON.stringify({
            officeSelected
          })
        },
        {
          action: SAUCER_LOG_ACTION.LOGIN.FAILED
        }
      );
      this.authService.setIsLoggedIn(false);

      return false;
    } finally {
      this.spinnerService.hide();
    }

    const mode = this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode) || Mode.WEB;
    if(mode == Mode.TV){
      const loginSession = this.tvLoginSessionService.persistCurrentSession();
      const loginSessionString = JSON.stringify(loginSession);
      const androidBridge = (window as any).AndroidBridge;
      if (typeof androidBridge?.onWebLoginSuccess === 'function') {
        androidBridge.onWebLoginSuccess(loginSessionString);
      }
    }
    this.navService.navCheckSheet();
    return true;
  }

  /**
   * The selected office is now stored on the device. Refresh its log setting
   * immediately; the heartbeat repeats this check while the device is active.
   */
  private async updateLogEnabledForSelectedOffice(): Promise<void> {
    const logEnabled = await this.deviceLicensesService.updateLogEnableOffice();
    if (logEnabled !== null) {
      this.nativeDiagnosticLogService.setLogEnabled(logEnabled);
    }
  }

  async getOfficeType(officeCd:string) {
    let officeSelected = this.sessionContextService.getOfficeSelected<any>();
    this.officeType =  officeSelected.officeType ?? 2;
    // sau khi lấy được officetype thì lấy daiinfo dựa vào office type
    await this.getDaiInfo(officeCd, this.officeType);
    // sau khi lấy được daiInfo thì lấy valid schedule period
    await this.getValidSchedulePeriod();
  }

  async checkLatestSyncDateAndSyncData(latestSyncDate: string, officeCd: string, corpprefix: string) {
    try {
      let isTimeExceeded = this.checkTimeExceededInJapan(latestSyncDate);
      if (isTimeExceeded) {
        await this.initSyncDataCheckSheet(this.officeType);
        const latestSyncDateRes = await lastValueFrom(this.officeService.getLatestSyncDate(officeCd, corpprefix));
        const latestSyncDate = latestSyncDateRes?.result?.latestSyncDate ?? "";
        this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.lastSyncDate, latestSyncDate);
        this.timeService.setTimeSyncFireBase(latestSyncDate);
      }
    } catch (error) {
      console.error("Sync Data failed: ", error);
    }
  }

  checkTimeExceededInJapan(dateStr: string | null | undefined): boolean {
    try {
      if (!dateStr) {
        return true;
      }
      const inputTime = new Date(dateStr);
  
      // Kiểm tra inputTime có hợp lệ không
      if (isNaN(inputTime.getTime())) {
        return true;
      }
  
      // Lấy thời gian hiện tại ở Nhật Bản
      const currentTime = new Date();
      const currentJSTTime = new Date(currentTime.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
  
      new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));

      // Tính khoảng cách thời gian tính bằng phút
      const diffInMinutes = (currentJSTTime.getTime() - inputTime.getTime()) / (1000 * 60);
  
      return diffInMinutes > 3; // Kiểm tra có quá 3 phút không
    } catch (error) {
      return true;
    }
  }

  async initSyncDataCheckSheet(officeType: number) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const url = `${API_ENDPOINT.checkSheet.manualSyncCkdata}`;
    const body = {
      corpPrefix, 
      officeCd,
      officeType
    };
    return lastValueFrom(this.http.post(url, body));
  }

  async getDaiInfo(officeCd: string, officeType: number, isWaitingAvatarProcess: boolean = true) {
    try {
      const res = await lastValueFrom(this.officeService.getDaiInfo(officeCd, officeType, isWaitingAvatarProcess));

      if (res?.status === 200 && res?.result?.length > 0) {
        this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.daiInfo, res.result);
      }
    }
    catch (error) {
      this.authService.logout();
    }
  }

  async getValidSchedulePeriod() {
    try {
      const daiInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.daiInfo) || [];
      if (!daiInfo || daiInfo.length === 0) {
        return;
      }

      const daiCds = daiInfo.map((dai: any) => dai.daicd).filter((daicd: string) => daicd);
      if (daiCds.length === 0) {
        return;
      }

      // Get current time in format "yyyy-MM-dd HH:mm"
      const now = new Date();
      const jpTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
      const year = jpTime.getFullYear();
      const month = String(jpTime.getMonth() + 1).padStart(2, '0');
      const day = String(jpTime.getDate()).padStart(2, '0');
      const hours = String(jpTime.getHours()).padStart(2, '0');
      const minutes = String(jpTime.getMinutes()).padStart(2, '0');
      const anchorDate = `${year}-${month}-${day} ${hours}:${minutes}`;

      const res = await lastValueFrom(this.checkSheetService.getValidSchedulePeriod(daiCds, anchorDate));

      if (res?.status === 200 && res?.result) {
        this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.validSchedulePeriod, res.result);
      }
    } catch (error) {
      console.error("Get valid schedule period failed: ", error);
    }
  }

   async getTitleAfterLogic() {
      try {
      const res: any = await this.ttlConfigService.getTitleAfterLogin();
      if (res?.status === 200 && res?.result?.length > 0) {
      }
    } 
    catch (error) {
      console.log("Sync title after login error: ", error);
    } 
    finally {}
  }

  async updateCorpCd() {
    try {
      const res: any = await this.ttlConfigService.updateCorpCd();
      if (res?.status === 200 && res?.result?.length > 0) {
      }
    }
    catch (error) {
      console.log("Sync title after login error: ", error);
    }
  }
}
